import React from 'react';
import { useTranslation } from 'react-i18next';
import './GoogleAuthErrorHandler.css';

interface GoogleAuthErrorHandlerProps {
  error: string | null;
  onRetry?: () => void;
  onFallbackToRedirect?: () => void;
  onDismiss?: () => void;
  showFallbackOption?: boolean;
}

/**
 * Google Authentication Error Handler Component
 * Provides specific error messages and recovery options for Google authentication.
 *
 * The `error` prop carries an i18n key (e.g. `errors.firebase.popupBlocked`) or,
 * for backend-originated failures, verbatim text. Categorization is done on a
 * lowercased copy so it works for both the key tokens and the English wording.
 */
export const GoogleAuthErrorHandler: React.FC<GoogleAuthErrorHandlerProps> = ({
  error,
  onRetry,
  onFallbackToRedirect,
  onDismiss,
  showFallbackOption = false
}) => {
  const { t } = useTranslation();

  if (!error) {
    return null;
  }

  // Normalize so we can match both i18n key tokens (`popupBlocked`,
  // `networkRequestFailed`, ...) and any verbatim English message.
  const e = error.toLowerCase();

  // Check cancellation before popup-blocked: the `popupClosedByUser` /
  // `cancelledPopupRequest` keys also contain "popup", but they are
  // cancellations, not a blocked popup.
  const isUserCancellation = e.includes('cancel') || e.includes('closed');
  const isPopupBlocked = !isUserCancellation && (e.includes('popupblocked') || e.includes('blocked'));
  const isNetworkError = e.includes('network') || e.includes('connection');
  const isServiceUnavailable =
    e.includes('unavailable') ||
    e.includes('operationnot') ||
    e.includes('notallowed') ||
    e.includes('notsupported') ||
    e.includes('not enabled');

  const getErrorIcon = () => {
    if (isPopupBlocked) return '🚫';
    if (isNetworkError) return '🌐';
    if (isUserCancellation) return 'ℹ️';
    if (isServiceUnavailable) return '⚠️';
    return '❌';
  };

  const getErrorTitle = () => {
    if (isPopupBlocked) return t('googleError.titles.popupBlocked');
    if (isNetworkError) return t('googleError.titles.connectionIssue');
    if (isUserCancellation) return t('googleError.titles.cancelled');
    if (isServiceUnavailable) return t('googleError.titles.unavailable');
    return t('googleError.titles.generic');
  };

  const getErrorDescription = () => {
    if (isPopupBlocked) return t('googleError.descriptions.popupBlocked');
    if (isNetworkError) return t('googleError.descriptions.connectionIssue');
    if (isUserCancellation) return t('googleError.descriptions.cancelled');
    if (isServiceUnavailable) return t('googleError.descriptions.unavailable');
    return t('googleError.descriptions.generic');
  };

  const shouldShowRetry = !isUserCancellation;
  const shouldShowFallback = isPopupBlocked && showFallbackOption;

  return (
    <div className="google-auth-error" role="alert" aria-live="polite">
      <div className="google-auth-error__content">
        <div className="google-auth-error__header">
          <span className="google-auth-error__icon" aria-hidden="true">
            {getErrorIcon()}
          </span>
          <h3 className="google-auth-error__title">
            {getErrorTitle()}
          </h3>
        </div>

        <p className="google-auth-error__description">
          {getErrorDescription()}
        </p>

        <div className="google-auth-error__actions">
          {shouldShowRetry && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="google-auth-error__action google-auth-error__action--primary"
            >
              {t('googleError.retry')}
            </button>
          )}

          {shouldShowFallback && onFallbackToRedirect && (
            <button
              type="button"
              onClick={onFallbackToRedirect}
              className="google-auth-error__action google-auth-error__action--secondary"
            >
              {t('googleError.useAlternative')}
            </button>
          )}

          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="google-auth-error__action google-auth-error__action--tertiary"
            >
              {t('googleError.dismiss')}
            </button>
          )}
        </div>

        {isPopupBlocked && (
          <div className="google-auth-error__help">
            <details className="google-auth-error__details">
              <summary className="google-auth-error__details-summary">
                {t('googleError.howToAllowPopups')}
              </summary>
              <div className="google-auth-error__details-content">
                <ol className="google-auth-error__help-list">
                  <li>{t('googleError.help.step1')}</li>
                  <li>{t('googleError.help.step2')}</li>
                  <li>{t('googleError.help.step3')}</li>
                </ol>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};
